<template>
  <div id="full-report-preview">
    <b-container
      id="viewer_assurance"
      :class="preview_mode == true ? 'p-5 bg-white rounded' : 'px-5 py-5 bg-white'"
    >
      <b-card class="px-3">
        <template v-if="has_assurance_content && is_assurance_deployed">
          <template v-if="assurance_list_loaded === true">
            <template v-if="lang.toUpperCase() === 'KOR'">
              <md
                v-if="latest_assurance?.report_statement"
                class="statement-content"
                :contents="latest_assurance.report_statement"
              />
              <div class="pt-5 f-90 d-flex flex-column align-items-end mr-5 no-page-break-inside">
                <div
                  v-if="latest_assurance?.report_assurance_date"
                  class="mb-4"
                  style="width: 135px"
                >
                  {{ latest_assurance?.report_assurance_date.slice(0, 4) }}년
                  {{ latest_assurance?.report_assurance_date.slice(5, 7) }}월
                </div>
                <div
                  class="border-bottom-dark fw-300 pb-1 mb-3 d-flex justify-content-between align-items-end"
                  style="width: 135px"
                >
                  CEO <span class="fw-500 f-105">배 익 현</span>
                </div>
                <div class="mb-5 text-right" style="width: 135px">(주)퀀티파이드이에스지</div>
              </div>

              <hr />

              <div class="d-flex">
                <b-img
                  v-if="latest_assurance && latest_assurance.verification_img_path"
                  :src="$store.state.static_host + latest_assurance.verification_img_path"
                  class="mr-4"
                  style="width: 180px"
                />
                <b-img
                  v-else
                  src="/static_img/logos/aa1000as.jpg"
                  class="mr-4"
                  style="width: 180px"
                />
                <b-img src="/static_img/logos/QESG.svg" class="" style="width: 150px" />
              </div>
            </template>

            <template v-else>
              <h4 class="mb-4">Third-party verification opinion</h4>
              <md
                v-if="report_statement_e"
                class="statement-content"
                :contents="report_statement_e"
              />
              <div class="pt-5 f-90 d-flex flex-column align-items-end mr-5 no-page-break-inside">
                <div class="mb-4" style="width: 128px">
                  {{ getEngMonth(latest_assurance?.report_assurance_date.slice(5, 7)) }}
                  <span class="ml-1">{{
                    latest_assurance?.report_assurance_date.slice(0, 4)
                  }}</span>
                </div>
                <div
                  class="border-bottom-dark fw-300 pb-1 mb-3 d-flex justify-content-between align-items-end"
                  style="width: 128px"
                >
                  CEO <span class="fw-500 f-105">Ikhyun Bae</span>
                </div>
                <div class="mb-5 text-right" style="width: 128px">QuantifiedESG, Inc.</div>
              </div>

              <hr />

              <div class="d-flex">
                <b-img
                  v-if="latest_assurance && latest_assurance.verification_img_path"
                  :src="$store.state.static_host + latest_assurance.verification_img_path"
                  class="mr-4"
                  style="width: 180px"
                />
                <b-img
                  v-else
                  src="/static_img/logos/aa1000as.jpg"
                  class="mr-4"
                  style="width: 180px"
                />
                <b-img src="/static_img/logos/QESG.svg" class="" style="width: 150px" />
              </div>
            </template>
          </template>
          <template v-else>
            <b-spinner align="center" />
          </template>
        </template>

        <template v-else>
          <div class="text-center py-5">
            <b-icon-exclamation-circle font-scale="3" variant="secondary" class="mb-3" />

            <!-- 검증의견서가 작성되지 않은 경우 -->
            <template v-if="!has_assurance_content">
              <h5 class="text-secondary mb-3">
                {{
                  lang.toUpperCase() === 'KOR'
                    ? '제3자 검증의견서가 작성되지 않았습니다'
                    : 'Independent Assurance Statement has not been written'
                }}
              </h5>
              <p class="text-muted">
                {{
                  lang.toUpperCase() === 'KOR'
                    ? '검증의견서를 작성해주세요.'
                    : 'Please write the assurance statement.'
                }}
              </p>
              <p class="text-muted mt-3">
                {{ lang.toUpperCase() === 'KOR' ? 'QESG로 문의하기: ' : 'Contact QESG: ' }}
                <a href="mailto:info@qesg.co.kr">info@qesg.co.kr</a>
              </p>
            </template>

            <!-- 검증의견서는 작성되었으나 배포되지 않은 경우 -->
            <template v-else>
              <h5 class="text-secondary mb-3">
                {{
                  lang.toUpperCase() === 'KOR'
                    ? '제3자 검증의견서가 배포되지 않았습니다'
                    : 'Independent Assurance Statement has not been published'
                }}
              </h5>
              <p class="text-muted">
                {{
                  lang.toUpperCase() === 'KOR'
                    ? '검증의견서 배포를 완료해주세요.'
                    : 'Please publish the assurance statement.'
                }}
              </p>
              <p class="text-muted mt-3">
                {{ lang.toUpperCase() === 'KOR' ? 'QESG로 문의하기: ' : 'Contact QESG: ' }}
                <a href="mailto:info@qesg.co.kr">info@qesg.co.kr</a>
              </p>
            </template>
          </div>
        </template>
      </b-card>
    </b-container>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

export default {
  name: 'ViewAssuranceStatement',
  props: {
    preview_mode: { type: Boolean, default: false }
  },
  computed: {
    ...mapState('report_list', ['lang']),
    ...mapState('assurance', ['assurance_list_loaded']),
    ...mapGetters('assurance', [
      'latest_assurance',
      'has_assurance_content',
      'is_assurance_deployed'
    ]),
    report_statement_e() {
      if (!this.assurance_list_loaded) return '';
      return this.latest_assurance?.report_statement_em
        ? this.latest_assurance.report_statement_em
        : this.latest_assurance?.report_statement_ea;
    }
  },
  methods: {
    getEngMonth(month) {
      if (month == '01') return 'January';
      if (month == '02') return 'February';
      if (month == '03') return 'March';
      if (month == '04') return 'April';
      if (month == '05') return 'May';
      if (month == '06') return 'June';
      if (month == '07') return 'July';
      if (month == '08') return 'August';
      if (month == '09') return 'September';
      if (month == '10') return 'October';
      if (month == '11') return 'November';
      if (month == '12') return 'December';
    }
  }
};
</script>

<style scoped>
.statement-content {
  white-space: normal;
}

.statement-content >>> img {
  max-width: 100%;
}
</style>
